import axios from 'axios'
import AXIOS_INSTANCE from './axiosInstance'
import type { DocumentSignature } from '../utils/signature'

export { PATIENT_SUMMARY_ENABLED } from '../utils/featureFlags'

// Display names for the optional local-language section — mirrors access-emr-core
// PatientSummaries::Languages::NAMES. Unknown codes fall back to the code itself.
export const PATIENT_SUMMARY_LANGUAGE_NAMES: Record<string, string> = {
  pcm: 'Nigerian Pidgin',
  yo: 'Yoruba',
  ha: 'Hausa',
  ig: 'Igbo',
  fr: 'French'
}

export const patientSummaryLanguageName = (code: string | null | undefined): string | null =>
  code ? (PATIENT_SUMMARY_LANGUAGE_NAMES[code] ?? code) : null

export type MedicationChangeType = 'new' | 'stopped' | 'changed' | 'continued'

export type MedicationChange = {
  key: string
  name: string | null
  strength: string | null
  dose_quantity: number | string | null
  dose_unit: string | null
  route: string | null
  frequency: string | null
  start_at: string | null
  end_at: string | null
  change: MedicationChangeType
}

/** A test ordered this visit and whether its result is in (2026-10-02 end-of-visit spec). */
export type TestStatus = 'ready' | 'pending'

export type VisitTest = {
  key?: string
  name: string
  status: TestStatus
}

/** Something the patient received in the clinic: a medicine given there, a completed procedure, a sample taken. */
export type ClinicGivenItem = {
  key: string
  kind: 'medicine' | 'procedure' | 'sample'
  name: string | null
  strength?: string | null
  dose_quantity?: number | string | null
  dose_unit?: string | null
  route?: string | null
  frequency?: string | null
}

export type VisitAppointment = {
  date: string | null
  start_time: string | null
  doctor_name: string | null
  purpose: string | null
}

export type HospitalStayFacts = {
  admitted_on: string | null
  /** The chosen discharge date once the instructions are completed (snapshot); live facts: only once the
   * encounter is completed. A draft panel shows PatientSummaryAttrs.discharge_date instead. */
  discharged_on: string | null
  days: number | null
  ward: string | null
}

export type VisitFacts = {
  medication_changes: MedicationChange[]
  /** Outpatient: each test with its status. Summaries completed before 2026-10-02 snapshot the names only. */
  tests: Array<VisitTest | string>
  appointments: VisitAppointment[]
  /** Outpatient only (absent on inpatient facts and older snapshots): what the patient received in the clinic. */
  clinic_given?: ClinicGivenItem[]
  patient: { name: string | null; age: number | null; ref: string | null }
  doctor_name: string | null
  visit_date: string | null
  /** Only on an inpatient encounter's facts (discharge instructions) — outpatient facts and older snapshots omit
   * both fields. */
  variant?: 'inpatient'
  hospital_stay?: HospitalStayFacts
  /** Completed documents only (snapshot facts): who completed it and when — the signature block. */
  signature?: DocumentSignature
}

export type PatientSummaryGenerationStatus = 'idle' | 'generating' | 'failed'

export type PatientSummaryAttrs = {
  status: 'draft' | 'completed'
  instructions_html: string | null
  language: string | null
  translation_html: string | null
  generation_status: PatientSummaryGenerationStatus
  // What is generating: an English draft or only a re-translation. Null when not generating.
  generation_mode: 'draft' | 'translate' | null
  generation_error: string | null
  generated_at: string | null
  edited_at: string | null
  completed_at: string | null
  /** Discharge instructions (inpatient) only: the chosen date, or the facility's today until one is chosen. */
  discharge_date?: string | null
  /** "Note from your doctor": the doctor's own text, printed last; never sent to the drafting AI. */
  doctor_note?: string | null
}

export type PatientSummaryRes = {
  // '' (empty string) for a summary that doesn't exist yet: the show endpoint serialises an unsaved
  // record, and jsonapi-serializable renders its nil id as "". Test it for truthiness, never for null.
  id: string
  type: 'patient_summary'
  attributes: PatientSummaryAttrs
}

export type PatientSummaryResponse = { data: PatientSummaryRes }

export type PatientSummaryShowMeta = {
  facts: VisitFacts
  enabled_languages: string[]
  latest_note_at: string | null
}

export type PatientSummaryShowResponse = PatientSummaryResponse & { meta: PatientSummaryShowMeta }

export type UpdatePatientSummaryAttrs = {
  instructions_html?: string
  language?: string | null
  discharge_date?: string
  doctor_note?: string
}

// Every error code the backend can return for the patient summary endpoints (staff + portal).
export type PatientSummaryErrorCode =
  | 'patient_summary_disabled'
  | 'completed'
  | 'edited_draft'
  | 'generating'
  | 'instructions_required'
  | 'translation_pending'
  | 'language_unavailable'
  | 'discharge_date_invalid'
  | 'discharge_date_in_future'

type JsonApiErrorBody = { errors?: Array<{ code?: string }> }

// Reads the jsonapi_errors `code` off an axios error so the UI can branch on it instead of parsing
// message strings. Returns undefined for anything that isn't a recognised API error response.
export const getPatientSummaryErrorCode = (err: unknown): PatientSummaryErrorCode | undefined => {
  if (!axios.isAxiosError(err)) return undefined
  const body = err.response?.data as JsonApiErrorBody | undefined
  return body?.errors?.[0]?.code as PatientSummaryErrorCode | undefined
}

const encounterSummaryPath = (encounterId: string) => `encounters/${encounterId}/patient_summary`

// GET /api/encounters/:encounter_id/patient_summary — a not-yet-created summary comes back as an
// unsaved 'draft' with data.id "" (empty string) and meta.facts computed live from the record.
export const getPatientSummary = async (encounterId: string) => {
  const { data } = await AXIOS_INSTANCE.get<PatientSummaryShowResponse>(encounterSummaryPath(encounterId))
  return data
}

// POST .../patient_summary/generate — force replaces a doctor-edited draft (see 409 edited_draft).
// language (optional): an enabled code — the same job drafts English and translates it — or null for
// English only; left out, the summary keeps its saved language. 422 language_unavailable if not enabled.
export const generatePatientSummary = async (encounterId: string, force = false, language?: string | null) => {
  const body = language === undefined ? { force } : { force, language }
  const { data } = await AXIOS_INSTANCE.post<PatientSummaryResponse>(
    `${encounterSummaryPath(encounterId)}/generate`,
    body
  )
  return data.data
}

// PATCH .../patient_summary  { patient_summary: { instructions_html?, language?, discharge_date?, doctor_note? } } — plain JSON body,
// not a JSON:API document (the backend reads params.expect(patient_summary: [...])).
export const updatePatientSummary = async (encounterId: string, attrs: UpdatePatientSummaryAttrs) => {
  const { data } = await AXIOS_INSTANCE.patch<PatientSummaryResponse>(encounterSummaryPath(encounterId), {
    patient_summary: attrs
  })
  return data.data
}

export const completePatientSummary = async (encounterId: string) => {
  const { data } = await AXIOS_INSTANCE.post<PatientSummaryResponse>(`${encounterSummaryPath(encounterId)}/complete`)
  return data.data
}

export const reopenPatientSummary = async (encounterId: string) => {
  const { data } = await AXIOS_INSTANCE.post<PatientSummaryResponse>(`${encounterSummaryPath(encounterId)}/reopen`)
  return data.data
}

// Portal (patient-facing) — completed summaries only.
export type PortalVisitSummaryAttrs = {
  encounter_id: number
  completed_at: string
  visit_date: string | null
  doctor_name: string | null
}

export type PortalVisitSummaryRes = {
  id: string
  type: 'patient_summary'
  attributes: PortalVisitSummaryAttrs
}

export type PortalVisitSummariesResponse = {
  data: PortalVisitSummaryRes[]
  meta: { pagination: unknown }
}

export type PatientSummarySnapshot = {
  facts: VisitFacts
  instructions_html: string | null
  translation_html: string | null
  language: string | null
  completed_by: string | null
  /** Snapshots completed before 2026-10-02 have none. */
  doctor_note?: string | null
}

export type PortalVisitSummaryShowResponse = {
  data: PortalVisitSummaryRes
  meta: { snapshot: PatientSummarySnapshot }
}

// GET /api/patient/visit_summaries
export const getPortalVisitSummaries = async () => {
  const { data } = await AXIOS_INSTANCE.get<PortalVisitSummariesResponse>('patient/visit_summaries')
  return data
}

// GET /api/patient/visit_summaries/:id
export const getPortalVisitSummary = async (id: string) => {
  const { data } = await AXIOS_INSTANCE.get<PortalVisitSummaryShowResponse>(`patient/visit_summaries/${id}`)
  return data
}
